import { createAction } from 'typesafe-actions';

import { type ArtistPandoraId, type PandoraId, type PandoraIdPayload } from 'src/types/shared';

import * as CatalogConstants from './constants';
import * as D from './deps';

interface CatalogGetDetailsPayload {
  pandoraId: PandoraId;
  redirectOnNotFound: boolean;
  withCollaborations: boolean;
}

export const getDetails = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_GET_DETAILS,
  (pandoraId: PandoraId, redirectOnNotFound = false, withCollaborations = false) => ({
    pandoraId,
    redirectOnNotFound,
    withCollaborations,
  }),
)<CatalogGetDetailsPayload>();
export type CatalogGetDetailsAction = ReturnType<typeof getDetails>;

interface CatalogRefreshAnnotationsPayload extends PandoraIdPayload {}
export const refreshAnnotations = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_REFRESH_ANNOTATIONS,
  (pandoraId: PandoraId) => {
    if (!pandoraId) {
      throw new Error('refreshAnnotations(): no pandoraId provided');
    }
    return { pandoraId };
  },
)<CatalogRefreshAnnotationsPayload>();
export type CatalogRefreshAnnotationsAction = ReturnType<typeof refreshAnnotations>;

interface CatalogGetArtistDiscographyPayload {
  artistPandoraId: ArtistPandoraId;
  annotationLimit: $TSFixMe;
  withCollaborations: $TSFixMe;
}

interface CatalogGetFullLyricsParams {
  returnExplicit: boolean;
  trackPandoraId: PandoraId;
}
type CatalogGetFullLyricsPayload = CatalogGetFullLyricsParams;

export const getFullLyrics = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_GET_FULLLYRICS,
  (params: CatalogGetFullLyricsParams) => params,
)<CatalogGetFullLyricsPayload>();
export type CatalogGetFullLyricsAction = ReturnType<typeof getFullLyrics>;

export const getArtistDiscography = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_GET_ARTIST_DISCOGRAPHY,
  (artistPandoraId: ArtistPandoraId, annotationLimit: $TSFixMe, withCollaborations = false) => ({
    artistPandoraId,
    annotationLimit,
    withCollaborations,
  }),
)<CatalogGetArtistDiscographyPayload>();
export type CatalogGetArtistDiscographyAction = ReturnType<typeof getArtistDiscography>;

interface CatalogGetArtistEventsPayload {
  artistPandoraId: ArtistPandoraId;
}

export const getArtistEvents = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_GET_ARTIST_EVENTS,
  (artistPandoraId: ArtistPandoraId) => ({ artistPandoraId }),
)<CatalogGetArtistEventsPayload>();
export type CatalogGetArtistEventsAction = ReturnType<typeof getArtistEvents>;

interface CatalogGetAllArtistTracksPayload {
  artistPandoraId: ArtistPandoraId;
  annotationLimit: $TSFixMe;
  withCollaborations: $TSFixMe;
}

export const getAllArtistTracks = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_GET_ALL_ARTIST_TRACKS,
  (artistPandoraId: ArtistPandoraId, annotationLimit: $TSFixMe, withCollaborations = false) => ({
    artistPandoraId,
    annotationLimit,
    withCollaborations,
  }),
)<CatalogGetAllArtistTracksPayload>();
export type CatalogGetAllArtistTracksAction = ReturnType<typeof getAllArtistTracks>;

interface OnDemandGetArtistAllSongsPayload extends PandoraIdPayload {}
export const getArtistAllSongs = createAction(
  CatalogConstants.OnDemandActions.ON_DEMAND_GET_ARTIST_ALL_SONGS,
  (pandoraId: PandoraId) => ({ pandoraId }),
)<OnDemandGetArtistAllSongsPayload>();
export type OnDemandGetArtistAllSongsAction = ReturnType<typeof getArtistAllSongs>;

interface OnDemandGetArtistTopSongsPayload extends PandoraIdPayload {}

export const getArtistTopSongs = createAction(
  CatalogConstants.OnDemandActions.ON_DEMAND_GET_ARTIST_TOP_SONGS,
  (pandoraId: PandoraId) => ({ pandoraId }),
)<OnDemandGetArtistTopSongsPayload>();
export type OnDemandGetArtistTopSongsAction = ReturnType<typeof getArtistTopSongs>;

interface CatalogAnnotateObjectsSimplePayload {
  ids: $TSFixMe;
  annotateAlbumTracks: $TSFixMe;
}

export const annotateObjectsSimple = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_ANNOTATE_OBJECTS_SIMPLE,
  (ids: $TSFixMe, annotateAlbumTracks = false) => ({ ids, annotateAlbumTracks }),
)<CatalogAnnotateObjectsSimplePayload>();
export type CatalogAnnotateObjectsSimpleAction = ReturnType<typeof annotateObjectsSimple>;

interface CatalogSetCurrentSourceIdPayload extends PandoraIdPayload {}
export const setCurrentSourceId = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_SET_CURRENT_SOURCE_ID,
  (pandoraId: PandoraId) => ({ pandoraId }),
)<CatalogSetCurrentSourceIdPayload>();
export type CatalogSetCurrentSourceIdAction = ReturnType<typeof setCurrentSourceId>;

interface OnDemandGetAlbumPayload extends PandoraIdPayload {}
export const getAlbum = createAction(
  CatalogConstants.OnDemandActions.ON_DEMAND_GET_ALBUM,
  (pandoraId: PandoraId) => ({ pandoraId }),
)<OnDemandGetAlbumPayload>();
export type OnDemandGetAlbumAction = ReturnType<typeof getAlbum>;

interface OnDemandGetTrackPayload extends PandoraIdPayload {}
export const getTrack = createAction(
  CatalogConstants.OnDemandActions.ON_DEMAND_GET_TRACK,
  (pandoraId: PandoraId) => ({ pandoraId }),
)<OnDemandGetTrackPayload>();
export type OnDemandGetTrackAction = ReturnType<typeof getTrack>;

interface CatalogSetPlaylistsParams {
  playlists: $TSFixMe;
}

type CatalogSetPlaylistsPayload = CatalogSetPlaylistsParams;

export const setPlaylistAnnotations = createAction(
  D.Constants.Catalog.CatalogActions.CATALOG_SET_PLAYLISTS,
  ({ playlists }: CatalogSetPlaylistsParams) => ({ playlists }),
)<CatalogSetPlaylistsPayload>();
export type CatalogSetPlaylistsAction = ReturnType<typeof setPlaylistAnnotations>;
